import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData, isSameData } from '../dto/dto.js';
import { Address } from './address.js';
import { PhoneNumber } from './phone-number.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { InvoiceType } from './invoicing/invoice-type.js';

export class BillingProfile extends DataModel {
  static readonly TYPE_ID: string = 'c75a774b18044543b04999a3720417e6';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly invoiceType: InvoiceType,
    public readonly name: string,
    public readonly email: string,
    public readonly taxCode: string | null,
    public readonly groupTaxCode: string | null,
    public readonly address: Address | null,
    public readonly phoneNumber: PhoneNumber | null,
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return BillingProfile.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, BillingProfile.SCHEMA_VERSION);
  }

  static create(options: {
    invoiceType: InvoiceType,
    name: string,
    email: string,
    taxCode: string | null,
    groupTaxCode: string | null,
    address: Address | null,
    phoneNumber: PhoneNumber | null,
  }): BillingProfile {
    return new BillingProfile(
        options.invoiceType,
        options.name,
        options.email,
        options.taxCode,
        options.groupTaxCode,
        options.address,
        options.phoneNumber,
    );
  }

  static personal(options: {
    name: string,
    email: string,
    phoneNumber: PhoneNumber | null,
    address: Address | null,
  }): BillingProfile {
    return BillingProfile.create({
      invoiceType: InvoiceType.PhysicalPerson,
      name: options.name,
      email: options.email,
      taxCode: null,
      groupTaxCode: null,
      address: options.address,
      phoneNumber: options.phoneNumber,
    });
  }

  static legalEntity(options: {
    name: string,
    email: string,
    taxCode: string,
    groupTaxCode: string | null,
    phoneNumber: PhoneNumber,
    address: Address,
  }): BillingProfile {
    return BillingProfile.create({
      invoiceType: InvoiceType.LegalEntity,
      name: options.name,
      email: options.email,
      taxCode: options.taxCode,
      groupTaxCode: options.groupTaxCode,
      address: options.address,
      phoneNumber: options.phoneNumber,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): BillingProfile {
    return new BillingProfile(
        toEnumFromString(MapUtils.get<string>(map, 'invoiceType'), InvoiceType),
        MapUtils.get<string>(map, 'name'),
        MapUtils.get<string>(map, 'email'),
        MapUtils.getOrNull<string>(map, 'taxCode'),
        MapUtils.getOrNull<string>(map, 'groupTaxCode'),
        context.fromDataOrNull<Address>(MapUtils.getOrNull<SerializedData>(map, 'address')),
        PhoneNumber.decode(MapUtils.getOrNull<string>(map, 'phoneNumber')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'name': this.name,
      'email': this.email,
      'phoneNumber': this.phoneNumber?.encode() ?? null,
      'address': context.toDataOrNull(this.address),
      'taxCode': this.taxCode,
      'groupTaxCode': this.groupTaxCode,
      'invoiceType': fromEnumAsString(this.invoiceType),
    };
  }

  clone(): this {
    return new BillingProfile(
        this.invoiceType,
        this.name,
        this.email,
        this.taxCode,
        this.groupTaxCode,
        this.address?.clone() ?? null,
        this.phoneNumber?.clone() ?? null,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.name === this.name &&
        other.email === this.email &&
        other.taxCode === this.taxCode &&
        other.groupTaxCode === this.groupTaxCode &&
        other.invoiceType === this.invoiceType &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        isSameData(other.address, this.address);
  }

  copyWith(data: Partial<BillingProfile>): this {
    return new BillingProfile(
        data.invoiceType ?? this.invoiceType,
        data.name ?? this.name,
        data.email ?? this.email,
        data.taxCode ?? this.taxCode,
        data.groupTaxCode ?? this.groupTaxCode,
        data.address ?? this.address?.clone() ?? null,
        data.phoneNumber ?? this.phoneNumber?.clone() ?? null,
    ) as this;
  }
}

export class BillingProfileBuilder extends DataModelBuilder<BillingProfile> {
  build(context: SerializationContext, map: SerializedData): BillingProfile {
    return BillingProfile.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(BillingProfile.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(BillingProfile.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version [groupTaxCode] field was added.
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
